import React from "react";
import { useTranslation } from "react-i18next";
import { camelCase, isEmpty } from "lodash";
import KpiScoBadge, {
  KpiScoBadgeProps,
  KpiScoBadgeTone
} from "@/components/ui/KpiScoBadge";
import {
  getScoreKey,
  SCORE_CONFIGS,
  ScoreConfig,
  ScoreType
} from "@/pages/deals/scoreConfig";

export interface DealScoreBadgeProps
  extends Omit<KpiScoBadgeProps, "label" | "tone"> {
  scoreType: ScoreType;
  scoreText: string;
}

const getTone = (
  scoreKey: ScoreConfig,
  scoreType: ScoreType
): KpiScoBadgeTone => {
  if (scoreType === ScoreType.POC) return "selected";
  switch (scoreKey) {
    case ScoreConfig.PASS:
    case ScoreConfig.FULL_PASS:
      return "positive";
    case ScoreConfig.FAILED:
      return "negative";
    default:
      return "neutral";
  }
};

/** A deal score as a badge: resolves the score's label and tone, then renders KpiScoBadge. */
export const DealScoreBadge = React.forwardRef<
  HTMLDivElement,
  DealScoreBadgeProps
>(({ scoreType, scoreText, ...badgeProps }, ref) => {
  const { t } = useTranslation();
  const scoreKey = getScoreKey(scoreText);
  const scoreTranslationKey = SCORE_CONFIGS[scoreType]?.valueMapping[scoreKey];
  if (isEmpty(scoreTranslationKey)) return null;

  return (
    <KpiScoBadge
      ref={ref}
      label={t(
        `deals.kpis.${camelCase(scoreType)}Scores.${scoreTranslationKey}`
      )}
      tone={getTone(scoreKey, scoreType)}
      {...badgeProps}
    />
  );
});
DealScoreBadge.displayName = "DealScoreBadge";
